import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";

import { getUrl } from "../../helpers/url";

const initialState = {
  settingsActiveTab: "contact",
  contactActiveTab: "syncRules",
  invoiceActiveTab: "syncRules",
  errorMessages: [],
  successMessage: "",
  isEnvPreferencesSaved: false,
  isMsbcConnected: false,
  isMsbcExtensionInstalled: false,
  environmentPreference: undefined,
  companyPreference: undefined,
  saveModalPreference: false,
  environment: {
    loading: false,
    environmentDetails: {},
    currentRequestId: undefined,
    error: "",
  },
  company: {
    loading: false,
    companyDetails: {},
    error: "",
    currentRequestId: undefined,
  },
  setupCheck: {
    loading: false,
    status: {},
    error: "",
    currentRequestId: undefined,
  },
  selectedTabValues: {},
  checkExtensionInstalled: false,
  showExtensionModal: false,
};

/**
 * This function is responsible for fetching environment
 */
export const fetchEnvironment = createAsyncThunk(
  "preference/fetchEnvironment",
  async (account) => {
    try {
      const { data: response } = await axios.get(
        `${getUrl("REACT_APP_GET_ENVIRONMENT")}?account=${account}`
      );
      return response;
    } catch (error) {
      console.log(error);
      let errorMessage;
      if (error.response && error.response.data.message)
        errorMessage = error.response.data.message;
      else errorMessage = error.message;
      toast.error(errorMessage);
      throw error;
    }
  }
);

/**
 * This function is responsible for fetching companies
 */
export const fetchCompanies = createAsyncThunk(
  "preference/fetchCompanies",
  async ({ account, environmentPreference }) => {
    try {
      const { data: response } = await axios.get(
        `${getUrl(
          "REACT_APP_GET_COMPANIES"
        )}?account=${account}&environment=${environmentPreference}`
      );
      return response;
    } catch (error) {
      console.log(error);
      let errorMessage;
      if (error.response && error.response.data.message)
        errorMessage = error.response.data.message;
      else errorMessage = error.message;
      toast.error(errorMessage);
      throw error;
    }
  }
);

/**
 * This function is responsible for fetching the status
 */
export const fetchStatus = createAsyncThunk(
  "preference/fetchStatus",
  async (account) => {
    try {
      const { data: response } = await axios.get(
        `${getUrl("REACT_APP_FETCH_STATUS")}?account=${account}`
      );

      return response;
    } catch (error) {
      console.log(error);
      let errorMessage;
      if (error.response && error.response.data.message)
        errorMessage = error.response.data.message;
      else errorMessage = error.message;
      toast.error(errorMessage);
      throw error;
    }
  }
);

export const preferenceSlice = createSlice({
  name: "preference",
  initialState,
  reducers: {
    /**
     * @description Reducer updates the states of sync settings active tab keys when user selects the tab or clicked on next button
     */
    onChangeSettingsActiveTab(state, { payload }) {
      if (
        state.isMsbcConnected &&
        !payload.stripeLoading &&
        !payload.msbcLoading &&
        state.settingsActiveTab === "contact" &&
        payload.selectedTab === "invoice" &&
        (!payload.isContactMappingsSaved ||
          !payload.isContactDefaultMappingsSaved ||
          !payload.isContactSyncRulesSaved)
      ) {
        state.selectedTabValues = {
          tab: payload.selectedTab,
          subTab: "syncRules",
        };
        state.saveModalPreference = true;
      } else if (
        state.isMsbcConnected &&
        !payload.stripeLoading &&
        !payload.msbcLoading &&
        state.settingsActiveTab === "invoice" &&
        payload.selectedTab === "contact" &&
        (!payload.isInvoiceSyncRulesSaved || !payload.isInvoiceMappingsSaved)
      ) {
        state.selectedTabValues = {
          tab: payload.selectedTab,
          subTab: "syncRules",
        };
        state.saveModalPreference = true;
      } else {
        state.settingsActiveTab = payload.selectedTab;
      }
    },
    /**
     * @description Reducer updates the states of contact active tab keys when user selects the tab or clicked on next button
     */
    onChangeContactActiveTab(state, { payload }) {
      if (
        state.isMsbcConnected &&
        !payload.stripeLoading &&
        !payload.msbcLoading &&
        state.contactActiveTab === "syncRules" &&
        !payload.isContactSyncRulesSaved &&
        (payload.selectedTab === "defaultMappings" ||
          payload.selectedTab === "fieldMappings")
      ) {
        state.selectedTabValues = {
          tab: "contact",
          subTab: payload.selectedTab,
        };
        state.saveModalPreference = true;
      } else if (
        state.isMsbcConnected &&
        !payload.stripeLoading &&
        !payload.msbcLoading &&
        state.contactActiveTab === "defaultMappings" &&
        (payload.selectedTab === "syncRules" ||
          payload.selectedTab === "fieldMappings") &&
        !payload.isContactDefaultMappingsSaved
      ) {
        state.selectedTabValues = {
          tab: "contact",
          subTab: payload.selectedTab,
        };
        state.saveModalPreference = true;
      } else if (
        state.isMsbcConnected &&
        !payload.stripeLoading &&
        !payload.msbcLoading &&
        state.contactActiveTab === "fieldMappings" &&
        !payload.isMappingsSaved &&
        (payload.selectedTab === "defaultMappings" ||
          payload.selectedTab === "syncRules")
      ) {
        state.selectedTabValues = {
          tab: "contact",
          subTab: payload.selectedTab,
        };
        state.saveModalPreference = true;
      } else {
        state.contactActiveTab = payload.selectedTab;
      }
    },
    /**
     * @description Reducer updates the states of invoice active tab keys when user selects the tab or clicked on next button
     */
    onChangeInvoiceActiveTab(state, { payload }) {
      if (
        state.isMsbcConnected &&
        !payload.stripeLoading &&
        !payload.msbcLoading &&
        state.invoiceActiveTab === "syncRules" &&
        !payload.isInvoiceSyncRulesSaved &&
        payload.selectedTab === "fieldMappings"
      ) {
        state.selectedTabValues = {
          tab: "invoice",
          subTab: payload.selectedTab,
        };
        state.saveModalPreference = true;
      } else if (
        state.isMsbcConnected &&
        !payload.stripeLoading &&
        !payload.msbcLoading &&
        state.invoiceActiveTab === "fieldMappings" &&
        !payload.isInvoiceMappingsSaved &&
        payload.selectedTab === "syncRules"
      ) {
        state.selectedTabValues = {
          tab: "invoice",
          subTab: payload.selectedTab,
        };
        state.saveModalPreference = true;
      } else {
        state.invoiceActiveTab = payload.selectedTab;
      }
    },
    /**
     * @description Reducer updates the states of success modal and shows the success modal with message
     */
    setShowSuccessModal(state, action) {
      state.successMessage = action.payload.message;
    },
    /**
     * @description Reducer updates the states of error modal and hides the error modal when user clicked on the page
     */
    onHideSuccessModal(state) {
      state.successMessage = "";
    },
    /**
     * @description Reducer updates the states of error modal and shows the error modal with message
     */
    setShowErrorModal(state, action) {
      state.errorMessages.push(action.payload.message);
    },
    /**
     * @description Reducer updates the states of success modal and hides the success modal when user clicked on the page
     */
    onHideErrorModal(state) {
      state.errorMessages.shift();
    },
    /**
     * @description Reducer updates the state that is used for checking msbc oauth
     */
    connectedMsbc(state, action) {
      state.isMsbcConnected = action.payload.isConnected;
    },
    /**
     * Reducer updates the state of the isMsbcExtensionInstalled
     */
    onChangeMsbcExtensionInstalled(state, { payload }) {
      state.isMsbcExtensionInstalled = payload.isConnected;
    },
    /**
     * Reducer updates the state of the dropdown present in the environment window
     */
    setDropdownPreferences(state, { payload }) {
      if (payload.dropdownFor === "environment") {
        state.environmentPreference = payload.selectedOption;
      } else if (payload.dropdownFor === "company") {
        state.companyPreference = payload.selectedOption;
      }
      state.isEnvPreferencesSaved = false;
    },
    /**
     * Reducer updates the state of the isEnvPreferencesSaved when user clicks on Save button
     */
    setIsEnvPreferencesSaved(state, { payload }) {
      state.isEnvPreferencesSaved = true;
    },
    /**
     * Reducer updates the state of the saveModalPreference when user changes the tabs
     */
    onChangeSaveModalPreference(state, { payload }) {
      state.saveModalPreference = payload.show;
    },
    /**
     * @description Reducer updates the state of tab and subTab when user clicks on save from the prompt
     */
    onChangeTabValues(state) {
      window.scroll(0, 0);
      if (state.selectedTabValues.tab === "contact") {
        state.settingsActiveTab = state.selectedTabValues.tab;
        state.contactActiveTab = state.selectedTabValues.subTab;
      } else {
        state.settingsActiveTab = state.selectedTabValues.tab;
        state.invoiceActiveTab = state.selectedTabValues.subTab;
      }
    },
    onChangeCheckExtensionInstalled(state, { payload }) {
      state.checkExtensionInstalled = payload.connected;
    },
    setShowExtensionModal(state, { payload }) {
      state.showExtensionModal = payload.show;
    },
    onHideExtensionModal(state) {
      state.showExtensionModal = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchEnvironment.pending, (state, actions) => {
      if (!state.environment.loading) {
        state.environment.loading = true;
        state.environment.currentRequestId = actions.meta.requestId;
      }
    });
    builder.addCase(fetchEnvironment.fulfilled, (state, actions) => {
      if (
        state.environment.loading &&
        state.environment.currentRequestId === actions.meta.requestId
      ) {
        state.environment.environmentDetails = actions.payload;
        state.environment.loading = false;
        state.environment.error = "";
        state.environment.currentRequestId = undefined;
      }
    });
    builder.addCase(fetchEnvironment.rejected, (state, actions) => {
      if (
        state.environment.loading &&
        state.environment.currentRequestId === actions.meta.requestId
      ) {
        state.environment.loading = false;
        state.environment.environmentDetails = {};
        state.environment.error = actions.error.message ?? "";
        state.environment.currentRequestId = undefined;
      }
    });
    builder.addCase(fetchCompanies.pending, (state, actions) => {
      if (!state.company.loading) {
        state.company.loading = true;
        state.company.currentRequestId = actions.meta.requestId;
      }
    });
    builder.addCase(fetchCompanies.fulfilled, (state, actions) => {
      if (
        state.company.loading &&
        state.company.currentRequestId === actions.meta.requestId
      ) {
        state.company.loading = false;
        state.company.companyDetails = actions.payload;
        state.company.error = "";
        state.company.currentRequestId = undefined;
      }
    });
    builder.addCase(fetchCompanies.rejected, (state, actions) => {
      if (
        state.company.loading &&
        state.company.currentRequestId === actions.meta.requestId
      ) {
        state.company.loading = false;
        state.company.companyDetails = {};
        state.company.error = actions.error.message ?? "";
        state.company.currentRequestId = undefined;
      }
    });
    builder.addCase(fetchStatus.pending, (state, actions) => {
      if (!state.setupCheck.loading) {
        state.setupCheck.loading = true;
        state.setupCheck.currentRequestId = actions.meta.requestId;
      }
    });
    builder.addCase(fetchStatus.fulfilled, (state, actions) => {
      if (
        state.setupCheck.loading &&
        state.setupCheck.currentRequestId === actions.meta.requestId
      ) {
        state.setupCheck.loading = false;
        state.setupCheck.status = actions.payload;
        state.setupCheck.error = "";
        state.setupCheck.currentRequestId = undefined;

        const { creds, userData, extensionApp } = state.setupCheck.status;
        if (creds?.msbc) {
          state.isMsbcConnected = creds.msbc;
        }

        if (extensionApp?.installed) {
          state.isMsbcExtensionInstalled = extensionApp.installed;
          // state.checkExtensionInstalled = extensionApp.installed;
        }

        if (userData && Object.keys(userData).length > 0) {
          state.environmentPreference = userData.msbc.environment ?? undefined;
          state.companyPreference = userData.msbc.companyId ?? undefined;
        }

        if (state.environmentPreference && state.companyPreference) {
          state.isEnvPreferencesSaved = true;
        }
      }
    });
    builder.addCase(fetchStatus.rejected, (state, actions) => {
      if (
        state.setupCheck.loading &&
        state.setupCheck.currentRequestId === actions.meta.requestId
      ) {
        state.setupCheck.loading = false;
        state.setupCheck.status = {};
        state.setupCheck.error = actions.error.message;
        state.setupCheck.currentRequestId = undefined;
      }
    });
  },
});

export const {
  onChangeContactActiveTab,
  onChangeInvoiceActiveTab,
  onChangeSettingsActiveTab,
  setShowErrorModal,
  setShowSuccessModal,
  onHideErrorModal,
  onHideSuccessModal,
  connectedMsbc,
  setDropdownPreferences,
  setIsEnvPreferencesSaved,
  onChangeSaveModalPreference,
  onChangeTabValues,
  onChangeMsbcExtensionInstalled,
  onChangeCheckExtensionInstalled,
  setShowExtensionModal,
  onHideExtensionModal,
} = preferenceSlice.actions;

export default preferenceSlice.reducer;
